import { audiencerateApi } from '../../audiencerate';
import type { SendWhatsappMessageBody, WhatsappConversation, WhatsappConversationArgs, WhatsappMessage } from './types';

const base = (workspaceId: string) => `/v1/workspace/${workspaceId}/whatsapp/inbox`;

// ponytail: latest 100 conversations and 100 messages per thread; add ?offset / ?before paging when chats get long.
const PAGE = 100;

export const whatsappInboxApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    getWhatsappConversations: build.query<WhatsappConversation[], { workspaceId: string }>({
      query: ({ workspaceId }) => ({ url: `${base(workspaceId)}/conversations`, params: { limit: PAGE } }),
      providesTags: ['WhatsappConversations'],
    }),
    getWhatsappMessages: build.query<WhatsappMessage[], WhatsappConversationArgs>({
      query: ({ workspaceId, conversationId }) => ({
        url: `${base(workspaceId)}/conversations/${conversationId}/messages`,
        params: { limit: PAGE },
      }),
      providesTags: (result, error, args) => [{ type: 'WhatsappMessages', id: args.conversationId }],
    }),
    sendWhatsappMessage: build.mutation<WhatsappMessage, WhatsappConversationArgs & { body: SendWhatsappMessageBody }>({
      query: ({ workspaceId, conversationId, body }) => ({
        url: `${base(workspaceId)}/conversations/${conversationId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, args) => [{ type: 'WhatsappMessages', id: args.conversationId }, 'WhatsappConversations'],
    }),
    sendWhatsappMedia: build.mutation<WhatsappMessage, WhatsappConversationArgs & { file: File; mime: string; caption?: string }>({
      // The file goes as the raw body (fetchBaseQuery only JSON-encodes plain objects), like templateImages.ts.
      query: ({ workspaceId, conversationId, file, mime, caption }) => ({
        url: `${base(workspaceId)}/conversations/${conversationId}/media`,
        method: 'POST',
        params: { filename: file.name, caption },
        body: file,
        headers: { 'Content-Type': mime },
      }),
      invalidatesTags: (result, error, args) => [{ type: 'WhatsappMessages', id: args.conversationId }, 'WhatsappConversations'],
    }),
    markWhatsappConversationRead: build.mutation<void, WhatsappConversationArgs>({
      query: ({ workspaceId, conversationId }) => ({
        url: `${base(workspaceId)}/conversations/${conversationId}/read`,
        method: 'POST',
      }),
      invalidatesTags: ['WhatsappConversations'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWhatsappConversationsQuery,
  useGetWhatsappMessagesQuery,
  useSendWhatsappMessageMutation,
  useSendWhatsappMediaMutation,
  useMarkWhatsappConversationReadMutation,
} = whatsappInboxApi;
